import {
  Radio,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';
import { dialogOverlay, StyledDialogActions, SuccessActionButton } from '../styles';
import { primary } from '@/theme/colors';
import { GenericModal } from '@/components/shared/GenericModal';
import { idmsTemplates } from '../data';
import { hexColorWithAlpha } from '@/utils';
import { IdmsUploadsTypeEnum } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { ValidateFileModal } from './ValidateFileModal';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { FileExceptionModal } from './FileExceptionModal';

import { PipeSupportModal } from './PipeSupportModal';
import { useIdmsData } from '@/hooks/useIdmsData';

type Props = {
  file: File | undefined;
  open: boolean;
  handleClose: () => void;
  title?: string;
  description?: string;
  fileInputReference: React.MutableRefObject<HTMLInputElement | null | undefined>;
};

export const FileTypeModal = ({
  file,
  open,
  handleClose,
  title,
  description,
  fileInputReference,
}: Props) => {
  const structureId = useGetStructureId();
  const {
    setFileExceptionsModal,
    setOpenConfirmationDialog,
    setSelectedFileType,
    handleXlsxFileUpload,
    handleCsvFileUpload,
    handleIdmsFileUpload,
    openConfirmationDialog,
    idmsUploadLoading,
    fileExceptionsModal,
    selectedFileType,
    isValidating,
    uploadedCsvParsedData,
    idmsValidationErrors,
  } = useIdmsData(structureId);

  const handleFileUpload = () => {
    if (!file || !selectedFileType) return;

    // Check if file is XLSX or CSV based on extension
    const isXlsx = file.name.toLowerCase().endsWith('.xlsx');

    if (isXlsx) {
      handleXlsxFileUpload(file, selectedFileType);
    } else {
      handleCsvFileUpload(file, selectedFileType);
    }
  };

  const handleCancelUpload = () => {
    setSelectedFileType(undefined);
    setOpenConfirmationDialog(false);
  };

  const handleFileValidation = () => {
    handleFileUpload();
    handleClose();
  };

  const handleFileTypeModalClose = () => {
    setSelectedFileType(undefined);
    handleClose();
  };

  const handleCloseFileExceptionModal = () => {
    setFileExceptionsModal(false);
    setSelectedFileType(undefined);
  };
  const handleCloseModals = () => {
    handleClose();
    setSelectedFileType(undefined);
  };
  const excludedNames = new Set(['APM Line Metadata', 'AF Inspection Priority']);

  return (
    <>
      <GenericModal
        open={open}
        handleClose={handleClose}
        title={title}
        description={description}
        crossIconStyle={{ backgroundColor: 'transparent', color: 'primary' }}
        sx={{ ...dialogOverlay, left: '37% !important', width: 536, minHeight: 572 }}
      >
        <List sx={{ maxHeight: 392, overflowY: 'auto' }}>
          {idmsTemplates
            .filter(({ name }) => !excludedNames.has(name))
            .map(({ name, type }) => (
              <ListItem
                data-testid="file-type-list-item"
                disableGutters
                key={type}
                sx={{
                  py: 0,
                  borderTop: 1,
                  borderColor: 'divider',
                  '&:hover': { backgroundColor: hexColorWithAlpha(primary.light, 20) },
                }}
              >
                <ListItemButton
                  sx={{ '&:hover': { background: 'none' } }}
                  onClick={() => setSelectedFileType(type)}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Radio checked={selectedFileType === type} />
                  </ListItemIcon>
                  <ListItemAvatar sx={{ minWidth: '30px', pl: 1, pr: 2 }}>
                    <img
                      src="/assets/icons/file-icon.svg"
                      alt="File_Preview"
                      width={30}
                      height={40}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '1.4rem', fontWeight: '600' }}
                    primary={name}
                  />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
        <StyledDialogActions>
          <Button onClick={handleFileTypeModalClose} variant="text" sx={{ mr: 1, height: '36px' }}>
            Cancel
          </Button>
          <SuccessActionButton
            variant="contained"
            onClick={handleFileValidation}
            disabled={!selectedFileType}
          >
            Confirm
          </SuccessActionButton>
        </StyledDialogActions>
      </GenericModal>

      <ValidateFileModal open={isValidating} />
      {selectedFileType === IdmsUploadsTypeEnum.PipeSupport && (
        <PipeSupportModal
          open={selectedFileType === IdmsUploadsTypeEnum.PipeSupport}
          handleClose={() => setSelectedFileType(undefined)}
          handleCloseModals={handleCloseModals}
          file={file}
          fileInputReference={fileInputReference}
        />
      )}

      <ConfirmationDialog
        title="Success"
        description="The file has been uploaded and validated successfully. Kindly confirm to proceed with importing the data into our system"
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
        isOpen={openConfirmationDialog}
        onConfirm={() => file && handleIdmsFileUpload(file)}
        onCancel={handleCancelUpload}
        handleClose={() => {}}
        loading={{
          state: idmsUploadLoading,
          text: 'Uploading...',
        }}
      />

      <FileExceptionModal
        open={fileExceptionsModal}
        idmsUploadLoading={idmsUploadLoading}
        selectedFileType={selectedFileType}
        handleIdmsFileUpload={handleIdmsFileUpload}
        handleClose={handleCloseFileExceptionModal}
        uploadedCsvParsedData={uploadedCsvParsedData}
        idmsValidationErrors={idmsValidationErrors}
        fileInputReference={fileInputReference}
        file={file}
      />
    </>
  );
};
